import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { api, fileUrl, formatApiErrorDetail } from "@/lib/api";
import NameplateSweepModal from "@/components/NameplateSweepModal";
import VendorSpendPill from "@/components/VendorSpendPill";
import {
  House, ArrowLeft, Key, ShieldCheck, Package, ClockCounterClockwise,
  Warning, Broadcast, ArrowSquareOut, CheckCircle, MapPin, WarningCircle,
  Microphone, Sparkle, Camera, CalendarBlank, Plus, Trash, FilePdf,
} from "@phosphor-icons/react";
import VoiceToPunchModal from "@/components/VoiceToPunchModal";
import PhotoVoicePunchModal from "@/components/PhotoVoicePunchModal";
import { PropertyFilesPanel } from "@/components/PropertyFilesPanel";
import PropertyEnrichCard from "@/components/PropertyEnrichCard";
import PastInspectionsStrip from "@/components/PastInspectionsStrip";

/**
 * UnitDetail — dedicated per-unit page.
 * URL: /admin/properties/:address/unit/:unit
 * Shows: entry codes + lockbox, vacancy/Ring status, work orders,
 * assets (both top-level db.assets and embedded property_configs.assets).
 */
export default function UnitDetail() {
  const { address: encodedAddr, unit: encodedUnit } = useParams();
  const address = decodeURIComponent(encodedAddr || "");
  const unit = decodeURIComponent(encodedUnit || "");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [projectsHere, setProjectsHere] = useState([]);
  const [voiceModal, setVoiceModal] = useState(null); // project | null
  const [photoVoiceModal, setPhotoVoiceModal] = useState(null); // project | null
  const [showProjectPicker, setShowProjectPicker] = useState(null); // "voice" | "photo" | null
  const [scanning, setScanning] = useState(false);
  const [showSweep, setShowSweep] = useState(false);
  const [showAddPicker, setShowAddPicker] = useState(false);
  const [addKind, setAddKind] = useState(null);   // "asset" | "meter"
  const [siblingUnits, setSiblingUnits] = useState([]);   // for the meter "serves" chips
  const [startingInspection, setStartingInspection] = useState(false);
  const navigate = useNavigate();

  // "Inspection Radar" — creates a fresh property-scoped inspection and
  // jumps to the wizard. Uses the same address-based helper as
  // PropertyDetailV2 since Unit views don't have a property ObjectId.
  const startInspection = async () => {
    if (!address || startingInspection) return;
    setStartingInspection(true);
    try {
      const { data: insp } = await api.post("/prop-inspections-by-address", {
        address,
        title: `Inspection · ${address} · Unit ${unit}`,
      });
      navigate(`/admin/inspections/${insp.inspection_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start inspection");
    } finally {
      setStartingInspection(false);
    }
  };

  const load = () => {
    api.get("/properties/unit-detail", { params: { address, unit }})
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load unit"));
  };

  const scanNameplate = async (file) => {
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: parsed } = await api.post("/property-assets/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.post("/property-assets", {
        address, unit,
        name: `${parsed.type || "Appliance"}${parsed.make ? " · " + parsed.make : ""}`.trim().slice(0, 80),
        type: parsed.type || "",
        make: parsed.make || "",
        model: parsed.model || "",
        serial: parsed.serial || "",
        location: `${address} · Unit ${unit}`,
        notes: parsed.notes || "",
        photo_path: parsed.photo_path || "",
      });
      toast.success(`✅ ${parsed.make || parsed.type || "Appliance"} · ${parsed.model || "(no model)"}${parsed.serial ? " · SN " + parsed.serial : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed — try a clearer photo"));
    } finally { setScanning(false); }
  };

  useEffect(() => {
    setData(null); setErr(null);
    api.get("/properties/unit-detail", { params: { address, unit }})
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Failed to load unit"));
    // Load ProjectRadar projects at this address (case-insensitive match)
    api.get("/projects").then(r => {
      const norm = (s) => (s || "").trim().toLowerCase();
      const matches = (r.data?.projects || []).filter(p => norm(p.property_address) === norm(address));
      setProjectsHere(matches);
    }).catch(() => setProjectsHere([]));
  }, [address, unit]);

  const openVoice = () => {
    if (!projectsHere.length) {
      toast.error("No ProjectRadar project exists at this address yet. Create one first.");
      return;
    }
    if (projectsHere.length === 1) setVoiceModal(projectsHere[0]);
    else setShowProjectPicker("voice");
  };
  const openPhotoVoice = () => {
    if (!projectsHere.length) {
      toast.error("No ProjectRadar project exists at this address yet. Create one first.");
      return;
    }
    if (projectsHere.length === 1) setPhotoVoiceModal(projectsHere[0]);
    else setShowProjectPicker("photo");
  };
  const pickProject = (proj) => {
    const kind = showProjectPicker;
    setShowProjectPicker(null);
    if (kind === "voice") setVoiceModal(proj);
    if (kind === "photo") setPhotoVoiceModal(proj);
  };

  const backLink = `/admin/properties/${encodeURIComponent(address)}`;

  if (err) {
    return (
      <AdminShell>
        <div className="p-8 max-w-3xl mx-auto text-center" data-testid="unit-detail-error">
          <WarningCircle weight="bold" size={40} className="mx-auto text-red-500"/>
          <div className="mt-2 font-display font-black text-2xl">{err}</div>
          <Link to={backLink} className="mt-4 inline-block text-[#00C4CC] font-black uppercase text-sm">← Back to property</Link>
        </div>
      </AdminShell>
    );
  }
  if (!data) return <AdminShell><div className="p-8 font-bold" data-testid="unit-detail-loading">Loading unit…</div></AdminShell>;

  return (
    <AdminShell>
      <div className="p-6 sm:p-8 max-w-6xl mx-auto" data-testid="unit-detail-root">
        <Link to={backLink} className="inline-flex items-center gap-1 text-xs font-black uppercase tracking-wider text-slate-500 hover:text-[#00C4CC]" data-testid="unit-back-link">
          <ArrowLeft weight="bold" size={12}/> {address}
        </Link>

        <div className="mt-2 flex items-center gap-3 flex-wrap">
          <House weight="bold" size={28} className="text-slate-800"/>
          <h1 className="font-display font-black text-2xl sm:text-3xl leading-tight">
            <span className="break-all">{address}</span>
            <span className="mx-2 text-slate-400">·</span>
            <span className="text-fuchsia-700">Unit {unit}</span>
          </h1>
          {data.vacancy_status && data.vacancy_status !== "occupied" && (
            <span data-testid="unit-vacancy-chip"
              className="px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-red-600 text-white">
              {(data.vacancy_status || "").replace("_", " ")}
            </span>
          )}
          {data.has_ring_alarm && (
            <span data-testid="unit-ring-chip"
              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-black uppercase tracking-widest bg-red-100 text-red-700 border border-red-300">
              <Broadcast weight="fill" size={11}/> Ring Alarm
            </span>
          )}
          <div className="ml-auto flex gap-2 flex-wrap">
            <OccupancyPill address={address} unit={unit} occupied={data.occupied}
              upcomingMoveIn={data.upcoming_move_in}
              upcomingMoveOut={data.upcoming_move_out}
              onChanged={(next) => setData({...data, ...next})}/>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/units/move-in-report.pdf?address=${encodeURIComponent(address)}&unit=${encodeURIComponent(unit)}&auth=${encodeURIComponent(localStorage.getItem("access_token") || localStorage.getItem("token") || "")}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="unit-move-in-pdf-btn"
              className="h-10 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <FilePdf weight="fill" size={13}/> Move-In Ready PDF
            </a>
            <button onClick={() => setShowSweep(true)} data-testid="unit-nameplate-sweep-btn"
              className="h-10 px-3 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Sparkle weight="fill" size={13}/> Nameplate Sweep
            </button>
            <button onClick={openVoice} data-testid="unit-voice-add-btn"
              className="h-10 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Microphone weight="fill" size={13}/> Voice Add Items
            </button>
            <button onClick={openPhotoVoice} data-testid="unit-photo-voice-btn"
              className="h-10 px-3 bg-violet-600 hover:bg-violet-700 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Sparkle weight="fill" size={13}/> Snap + Say
            </button>
            <button onClick={startInspection} disabled={startingInspection}
              data-testid="unit-inspection-radar-btn"
              className="h-10 px-3 bg-[#00C4CC] hover:bg-[#00e5ed] disabled:opacity-60 text-[#0F1F3A] font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
              <Sparkle weight="fill" size={13}/> {startingInspection ? "Starting…" : "Inspection Radar"}
            </button>
          </div>
        </div>

        {/* Project picker modal (only when multiple projects at same address) */}
        {showProjectPicker && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" data-testid="unit-project-picker">
            <div className="w-full max-w-md bg-white shadow-2xl">
              <div className="p-4 border-b-2 border-slate-200 flex items-center gap-2">
                <div className="flex-1 font-display font-black text-lg uppercase tracking-tight">Pick a Project</div>
                <button onClick={() => setShowProjectPicker(null)} className="text-slate-500 hover:text-slate-800">✕</button>
              </div>
              <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto">
                {projectsHere.map(p => (
                  <button key={p.id || p._id} onClick={() => pickProject(p)}
                    data-testid={`unit-pick-project-${p.id || p._id}`}
                    className="w-full text-left p-3 border-2 border-slate-300 hover:border-fuchsia-600 hover:bg-fuchsia-50">
                    <div className="font-black text-sm">{p.name}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest">{p.kind || "—"} · {p.status}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* KPI strip — Work Orders + Open tiles are TAPPABLE and scroll
            to the list below. Fixes the "I can't get to the WOs from
            the unit page" complaint. */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Kpi label="Work Orders" value={data.summary?.total_jobs || 0} testid="unit-kpi-jobs"
            hint={(data.summary?.total_jobs || 0) > 0 ? "Tap to open" : ""}
            onClick={(data.summary?.total_jobs || 0) > 0 ? () => {
              const el = document.getElementById("unit-jobs-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            } : undefined}/>
          <Kpi label="Open" value={data.summary?.open_jobs || 0}
            accent={data.summary?.open_jobs > 0 ? "text-amber-600" : "text-slate-500"}
            testid="unit-kpi-open"
            hint={(data.summary?.open_jobs || 0) > 0 ? "Tap to open" : ""}
            onClick={(data.summary?.open_jobs || 0) > 0 ? () => {
              const el = document.getElementById("unit-jobs-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            } : undefined}/>
          <Kpi label="Assets" value={data.summary?.assets_count || 0} testid="unit-kpi-assets"
            hint={(data.summary?.assets_count || 0) > 0 ? "Tap to open" : ""}
            onClick={(data.summary?.assets_count || 0) > 0 ? () => {
              const el = document.getElementById("unit-assets-section");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            } : undefined}/>
          <Kpi label="Turns" value={data.summary?.turns || 0} testid="unit-kpi-turns"/>
        </div>

        {/* Ring Alarm inline detail — only when installed */}
        {data.has_ring_alarm && data.ring_alarm && (
          <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-red-500" data-testid="unit-ring-card">
            <div className="flex items-center gap-2">
              <Broadcast weight="fill" size={20} className="text-red-600"/>
              <h3 className="font-display font-black text-sm uppercase tracking-wider">Ring Alarm · Installed</h3>
              <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
                {(data.ring_alarm.installed_at || "").slice(0, 10) || "—"}
              </span>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Kpi label="Sensors" value={data.ring_alarm.sensor_count || 0} testid="unit-ring-sensors"/>
              <Kpi label="Cameras" value={data.ring_alarm.camera_count || 0} testid="unit-ring-cameras"/>
              <Kpi label="Base Serial" value={data.ring_alarm.base_station_serial || "—"} small testid="unit-ring-base"/>
              <Kpi label="Last Event" value={(data.ring_alarm.last_event_at || "").slice(0, 10) || "None"} small testid="unit-ring-last"/>
            </div>
            {data.ring_alarm.notes && (
              <div className="mt-2 text-xs text-slate-600 whitespace-pre-wrap" data-testid="unit-ring-notes">{data.ring_alarm.notes}</div>
            )}
          </div>
        )}

        {/* Scheduled visits pinned to this unit from any ProjectRadar project */}
        {(data.scheduled_visits || []).length > 0 && (
          <UnitScheduledVisits visits={data.scheduled_visits} />
        )}

        <div className="mt-4">
          <VendorSpendPill address={address} unit={unit}/>
        </div>

        {/* Items Left Behind — junk / furniture / boxes still sitting in this unit */}
        <UnitLeftBehind address={address} unit={unit}
          visits={data.scheduled_visits || []}/>

        {/* Access + Codes */}
        <AccessCard
          address={address}
          unit={unit}
          lockbox_code={data.lockbox_code}
          entry_code={data.entry_code}
          entry_notes={data.entry_notes}
          key_tag_location={data.key_tag_location}
          onSaved={(patch) => setData({ ...data, ...patch })}
        />

        {/* Auto-enrich from public sources (Philly OPA + AI rent estimate) */}
        <div className="mt-4">
          <PropertyEnrichCard address={address} unit={unit}
            extraNotes={data.pm_notes_current || ""}
            onApplied={() => api.get("/properties/unit-detail", { params: { address, unit } }).then(r => setData(r.data))}/>
        </div>

        {/* PM Notes */}
        <PMNotesCard address={address} unit={unit}
          pm_notes_current={data.pm_notes_current || ""}
          pm_notes_log={data.pm_notes_log || []}
          onSaved={(next) => setData({ ...data, ...next })}/>

        {/* Property Files — shared across every unit at this address */}
        <PropertyFilesPanel address={address}/>

        {/* Past completed inspections at this address (scoped to unit if the title carries it). */}
        <div className="mt-4">
          <PastInspectionsStrip address={address} unit={unit}/>
        </div>

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Jobs */}
          <div id="unit-jobs-section" className="lg:col-span-2 rugged-card p-4 bg-white" data-testid="unit-jobs-card">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise weight="bold" size={20}/>
              <h2 className="font-display font-black text-lg uppercase tracking-wider">Work Orders</h2>
              <span className="text-xs text-slate-500 font-bold">· {(data.jobs || []).length}</span>
            </div>
            <div className="mt-3 space-y-2" data-testid="unit-jobs-list">
              {(data.jobs || []).map(j => (
                <Link key={j.id} to={`/admin/jobs/${j.id}`} data-testid={`unit-job-${j.id}`}
                  className="block p-3 border border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 transition-colors">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[10px] font-black uppercase tracking-wider font-mono bg-[#0f172a] text-white px-1.5 py-0.5">{j.work_order_number || "—"}</span>
                        {j.category && <span className="text-[10px] font-black uppercase tracking-wider bg-slate-200 text-slate-700 px-1.5 py-0.5">{j.category}</span>}
                        {j.is_turn && <span className="text-[10px] font-black uppercase tracking-wider bg-amber-500 text-white px-1.5 py-0.5">Turn ${Number(j.turn_flat_price || 0).toFixed(0)}</span>}
                        {j.status === "completed" ? (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-emerald-600 text-white px-1.5 py-0.5 inline-flex items-center gap-0.5"><CheckCircle weight="fill" size={9}/> Done</span>
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-slate-500 text-white px-1.5 py-0.5">{j.status}</span>
                        )}
                      </div>
                      <div className="mt-1 text-sm font-bold truncate">{j.title || "—"}</div>
                      <div className="mt-0.5 text-[10px] text-slate-500">
                        {(j.completed_at || j.created_at || "").slice(0, 10)}{j.assigned_to_name ? ` · ${j.assigned_to_name}` : ""}
                      </div>
                    </div>
                    <ArrowSquareOut weight="bold" size={14} className="text-slate-400 flex-shrink-0 mt-1"/>
                  </div>
                </Link>
              ))}
              {(data.jobs || []).length === 0 && (
                <div className="text-sm text-slate-500 font-bold" data-testid="unit-jobs-empty">No work orders logged for this unit yet.</div>
              )}
            </div>
          </div>

          {/* Right: Assets */}
          <div id="unit-assets-section" className="rugged-card p-4 bg-white" data-testid="unit-assets-card">
            <div className="flex items-center gap-2 flex-wrap">
              <Package weight="bold" size={20} className="text-fuchsia-600"/>
              <h2 className="font-display font-black text-lg uppercase tracking-wider">Assets</h2>
              <span className="text-xs text-slate-500 font-bold">· {(data.assets || []).length}</span>
              <div className="ml-auto flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-1.5 h-9 px-3 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
                  {scanning ? <><Sparkle weight="fill" size={13} className="animate-pulse"/> OCR…</>
                            : <><Camera weight="fill" size={13}/> Scan Nameplate</>}
                  <input type="file" accept="image/*" capture="environment" className="hidden"
                    disabled={scanning}
                    data-testid="unit-scan-nameplate-input"
                    onChange={e => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) scanNameplate(f);
                    }}/>
                </label>
                <button type="button" onClick={() => setShowAddPicker(true)}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-slate-900 hover:bg-slate-800 text-white font-black uppercase text-[10px] tracking-widest"
                  data-testid="unit-add-picker-btn">
                  <Plus weight="bold" size={13}/> Add
                </button>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 mt-1 font-bold uppercase tracking-widest">
              📷 Scan a nameplate OR tap Add for manual asset/meter entry
            </div>
            <div className="mt-3 space-y-2" data-testid="unit-assets-list">
              {(data.assets || []).map(a => (
                <Link key={a.id} to="/admin/assets" data-testid={`unit-asset-${a.id}`}
                  className="block p-2.5 border border-slate-300 hover:border-fuchsia-600 hover:bg-fuchsia-50 transition-colors">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-black text-sm truncate">{a.name || a.make || "(asset)"}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        {[a.make, a.model, a.serial].filter(Boolean).join(" · ") || "—"}
                      </div>
                      {a.location && <div className="text-[10px] text-slate-500 truncate">{a.location}</div>}
                    </div>
                    <ArrowSquareOut weight="bold" size={12} className="text-slate-400 flex-shrink-0"/>
                  </div>
                </Link>
              ))}
              {(data.assets || []).length === 0 && (
                <div className="text-sm text-slate-500 font-bold" data-testid="unit-assets-empty">No assets attached to this unit yet.</div>
              )}
            </div>

            {/* Meters (unit-scoped) */}
            <div className="mt-5 pt-4 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <Broadcast weight="bold" size={16} className="text-sky-600"/>
                <h3 className="font-display font-black text-sm uppercase tracking-wider">Utility Meters</h3>
                <span className="text-xs text-slate-500 font-bold">· {(data.meters || []).length}</span>
              </div>
              <div className="mt-2 space-y-2" data-testid="unit-meters-list">
                {(data.meters || []).map(m => (
                  <div key={m.id} className="p-2.5 border border-slate-300" data-testid={`unit-meter-${m.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-sm truncate">
                          {String(m.utility_type || "meter").toUpperCase()}
                          {m.provider ? ` · ${m.provider}` : ""}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {[m.meter_number && `#${m.meter_number}`, m.reading && `reading ${m.reading}`, m.location].filter(Boolean).join(" · ") || "—"}
                        </div>
                        {m.serves_units?.length ? (
                          <div className="text-[10px] text-emerald-700 font-bold">Serves: {m.serves_units.join(", ")}</div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ))}
                {(data.meters || []).length === 0 && (
                  <div className="text-sm text-slate-500 font-bold" data-testid="unit-meters-empty">No meters logged for this unit yet.</div>
                )}
              </div>
            </div>

            {/* Building Common rollup — shown on every non-Common unit */}
            {unit !== "Common" && ((data.common_assets?.length || 0) + (data.common_meters?.length || 0) > 0) && (
              <div className="mt-5 pt-4 border-t border-slate-200 bg-amber-50 -mx-4 -mb-4 px-4 pb-4 rounded-b-lg" data-testid="unit-common-rollup">
                <div className="flex items-center gap-2 flex-wrap">
                  <House weight="bold" size={16} className="text-amber-700"/>
                  <h3 className="font-display font-black text-sm uppercase tracking-wider text-amber-800">Building Common</h3>
                  <Link to={`/admin/properties/${encodeURIComponent(address)}/unit/${encodeURIComponent("Common")}`}
                    className="ml-auto text-[10px] font-black uppercase tracking-widest text-amber-700 hover:text-amber-900 underline"
                    data-testid="unit-common-link">
                    Open Common Area →
                  </Link>
                </div>
                <div className="mt-2 space-y-1">
                  {(data.common_assets || []).map(a => (
                    <div key={`ca-${a.id}`} className="text-xs text-amber-900 flex items-center gap-2" data-testid={`unit-common-asset-${a.id}`}>
                      <Package size={12} className="opacity-60"/>
                      <span className="font-bold">{a.name || a.category || "Asset"}</span>
                      <span className="opacity-70 truncate">{a.location}</span>
                    </div>
                  ))}
                  {(data.common_meters || []).map(m => (
                    <div key={`cm-${m.id}`} className="text-xs text-amber-900 flex items-center gap-2" data-testid={`unit-common-meter-${m.id}`}>
                      <Broadcast size={12} className="opacity-60"/>
                      <span className="font-bold">{String(m.utility_type || "meter").toUpperCase()}{m.provider ? ` · ${m.provider}` : ""}</span>
                      <span className="opacity-70 truncate">{[m.meter_number && `#${m.meter_number}`, m.location].filter(Boolean).join(" · ")}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Unit Timeline — digital twin */}
        <UnitTimeline jobs={data.jobs || []} assets={data.assets || []} pm_notes_log={data.pm_notes_log || []}
          lockbox_code={data.lockbox_code} entry_code={data.entry_code}/>
      </div>

      {voiceModal && (
        <VoiceToPunchModal
          projectId={voiceModal.id || voiceModal._id}
          projectName={voiceModal.name}
          rooms={voiceModal.rooms || []}
          defaultRoom={unit}
          onClose={() => setVoiceModal(null)}
          onAdded={() => setVoiceModal(null)}
        />
      )}
      {photoVoiceModal && (
        <PhotoVoicePunchModal
          fixedProjectId={photoVoiceModal.id || photoVoiceModal._id}
          fixedProjectName={photoVoiceModal.name}
          roomOverride={unit}
          onClose={() => setPhotoVoiceModal(null)}
          onSaved={() => setPhotoVoiceModal(null)}
        />
      )}
      {showSweep && (
        <NameplateSweepModal address={address} unit={unit}
          onClose={() => setShowSweep(false)}
          onDone={() => load()}/>
      )}
      {showAddPicker && (
        <AddPickerModal
          onClose={() => setShowAddPicker(false)}
          onPick={(k) => { setShowAddPicker(false); setAddKind(k); }}
        />
      )}
      {addKind === "asset" && (
        <AddAssetModal address={address} unit={unit}
          onClose={() => setAddKind(null)}
          onSaved={async () => { setAddKind(null); load(); }}/>
      )}
      {addKind === "meter" && (
        <AddMeterModal address={address} unit={unit}
          siblingUnits={data.sibling_units || []}
          onClose={() => setAddKind(null)}
          onSaved={async () => { setAddKind(null); load(); }}/>
      )}
    </AdminShell>
  );
}

function UnitTimeline({ jobs = [], assets = [], pm_notes_log = [], lockbox_code, entry_code }) {
  // Merge every event into one sorted timeline
  const events = [];
  jobs.forEach(j => {
    events.push({
      id: `job-${j.id}`, at: j.completed_at || j.created_at,
      kind: "job", icon: j.status === "completed" ? "✅" : "🛠️",
      title: j.title, sub: `WO ${j.work_order_number || "—"} · ${j.status}${j.assigned_to_name ? " · " + j.assigned_to_name : ""}`,
      color: j.status === "completed" ? "bg-emerald-500" : (j.is_turn ? "bg-amber-500" : "bg-slate-500"),
    });
  });
  assets.forEach(a => {
    if (a.install_date) events.push({
      id: `asset-${a.id}`, at: a.install_date,
      kind: "asset", icon: "📦",
      title: `Installed: ${a.name || a.make || "Asset"}`,
      sub: `${a.make || ""} ${a.model || ""} · Serial ${a.serial || "—"}`,
      color: "bg-fuchsia-500",
    });
  });
  pm_notes_log.forEach(n => events.push({
    id: `note-${n.id}`, at: n.at,
    kind: "note", icon: "📝",
    title: "PM Note",
    sub: `${n.by || "—"}: ${(n.text || "").slice(0, 140)}`,
    color: "bg-amber-500",
  }));
  events.sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? events : events.slice(0, 10);

  return (
    <div className="mt-6 rugged-card p-4 bg-white" data-testid="unit-timeline">
      <div className="flex items-center gap-2">
        <ClockCounterClockwise weight="bold" size={20}/>
        <h2 className="font-display font-black text-lg uppercase tracking-wider">Unit Timeline</h2>
        <span className="text-xs text-slate-500 font-bold">· {events.length} event{events.length === 1 ? "" : "s"}</span>
      </div>
      {events.length === 0 ? (
        <div className="mt-3 text-sm text-slate-500 font-bold" data-testid="unit-timeline-empty">
          No history yet. Job, asset, and PM-note activity will populate here automatically.
        </div>
      ) : (
        <div className="mt-4 relative">
          <div className="absolute left-4 top-2 bottom-2 w-0.5 bg-slate-200"/>
          <div className="space-y-3">
            {shown.map(e => (
              <div key={e.id} className="pl-10 relative" data-testid={`unit-timeline-${e.id}`}>
                <div className={`absolute left-2 top-1.5 h-5 w-5 rounded-full ${e.color} border-2 border-white flex items-center justify-center text-[10px]`}>{e.icon}</div>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{(e.at || "").slice(0, 10)} · {e.kind}</div>
                <div className="text-sm font-black text-slate-900 mt-0.5">{e.title}</div>
                <div className="text-xs text-slate-600 mt-0.5">{e.sub}</div>
              </div>
            ))}
          </div>
          {events.length > 10 && (
            <button onClick={() => setExpanded(!expanded)} data-testid="unit-timeline-toggle"
              className="mt-3 text-[10px] font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a]">
              {expanded ? "Show less ↑" : `Show all ${events.length} ↓`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}


function OccupancyPill({ address, unit, occupied, upcomingMoveIn, upcomingMoveOut, onChanged }) {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.put("/properties/unit-occupancy", { address, unit, occupied: !occupied });
      onChanged?.({ occupied: r.data.occupied, vacancy_status: r.data.vacancy_status });
      toast.success(r.data.occupied ? `🚫 ${unit} marked occupied — staff will avoid` : `${unit} marked vacant`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't update occupancy"));
    } finally { setBusy(false); }
  };
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const fmt = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return new Date(+y, +m - 1, +day).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  // Show a lead-time chip when a move-in is on the calendar within 14 days
  // AND the unit is not yet flagged occupied (backend auto-flips once the
  // date arrives, so this chip disappears the day of).
  if (upcomingMoveIn && !occupied) {
    const daysUntil = Math.round((new Date(upcomingMoveIn.date) - new Date(today)) / 86400000);
    if (daysUntil >= 0 && daysUntil <= 14) {
      return (
        <div data-testid="unit-move-in-scheduled"
          className="h-10 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 bg-indigo-600 text-white">
          📅 Move-in Scheduled · {fmt(upcomingMoveIn.date)}
          {daysUntil === 0 && <span className="ml-1 bg-white/25 px-1.5 py-0.5">TODAY</span>}
          {daysUntil > 0 && <span className="ml-1 opacity-80">· in {daysUntil}d</span>}
        </div>
      );
    }
  }
  // Show move-out lead-time chip too
  if (upcomingMoveOut && occupied) {
    const daysUntil = Math.round((new Date(upcomingMoveOut.date) - new Date(today)) / 86400000);
    if (daysUntil >= 0 && daysUntil <= 14) {
      return (
        <div data-testid="unit-move-out-scheduled"
          className="h-10 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 bg-amber-500 text-slate-900">
          📤 Move-out Scheduled · {fmt(upcomingMoveOut.date)}
          {daysUntil === 0 && <span className="ml-1 bg-slate-900 text-white px-1.5 py-0.5">TODAY</span>}
          {daysUntil > 0 && <span className="ml-1 opacity-80">· in {daysUntil}d</span>}
        </div>
      );
    }
  }
  return (
    <button onClick={toggle} disabled={busy} data-testid="unit-occupancy-toggle"
      className={`h-10 px-3 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 ${
        occupied ? "bg-red-600 hover:bg-red-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"
      }`}>
      {busy ? "…" : (occupied ? "🚫 Occupied · Tap to Vacate" : "🏠 Mark Occupied")}
    </button>
  );
}

function Kpi({ label, value, accent = "text-slate-900", small = false, testid, onClick, hint }) {
  const clickable = typeof onClick === "function";
  const Wrap = clickable ? "button" : "div";
  return (
    <Wrap
      onClick={clickable ? onClick : undefined}
      data-testid={testid}
      className={"p-3 border-2 bg-white text-left w-full " + (clickable
        ? "border-slate-300 hover:border-[#0f172a] hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer"
        : "border-slate-300")}>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
        {label}
        {clickable && <span className="text-slate-400">→</span>}
      </div>
      <div className={`mt-0.5 font-display font-black ${small ? "text-sm leading-tight" : "text-2xl"} ${accent}`}>{value}</div>
      {hint && <div className="text-[9px] font-black uppercase tracking-widest text-slate-400 mt-0.5">{hint}</div>}
    </Wrap>
  );
}

function AccessCard({ address, unit, lockbox_code, entry_code, entry_notes, key_tag_location, onSaved }) {
  const hasAny = !!(lockbox_code || entry_code || entry_notes || key_tag_location);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ lockbox_code: "", entry_code: "", entry_notes: "", key_tag_location: "" });
  const [saving, setSaving] = useState(false);

  const openEdit = () => {
    setDraft({
      lockbox_code: lockbox_code || "",
      entry_code: entry_code || "",
      entry_notes: entry_notes || "",
      key_tag_location: key_tag_location || "",
    });
    setEditing(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/properties/unit-access-codes", { address, unit, ...draft });
      onSaved?.(draft);
      toast.success("Access codes saved");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-[#00C4CC]" data-testid="unit-access-card">
      <div className="flex items-center gap-2">
        <Key weight="bold" size={20} className="text-[#00C4CC]"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Access · Codes</h3>
        {!editing && (
          <button onClick={openEdit} data-testid="unit-access-edit-btn"
            className="ml-auto h-8 px-3 border-2 border-[#0f172a] font-black uppercase text-[9px] tracking-widest hover:bg-slate-50">
            {hasAny ? "Edit" : "+ Add Codes"}
          </button>
        )}
      </div>
      {!editing && !hasAny && (
        <div className="mt-2 text-xs text-slate-500 font-bold" data-testid="unit-access-empty">
          No lockbox, entry code, or key tag location recorded. Tap <b>+ Add Codes</b> above — they'll auto-appear on every future work order at this unit.
        </div>
      )}
      {!editing && hasAny && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {key_tag_location && <Field label="🔑 Key Tag" value={key_tag_location} testid="unit-key-tag"/>}
          {lockbox_code && <Field label="Lockbox" value={lockbox_code} testid="unit-lockbox"/>}
          {entry_code && <Field label="Entry Code" value={entry_code} testid="unit-entry-code"/>}
          {entry_notes && <Field label="Entry Notes" value={entry_notes} multiline testid="unit-entry-notes"/>}
        </div>
      )}
      {editing && (
        <div className="mt-3 space-y-3" data-testid="unit-access-editor">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
              🔑 Key Tag Location <span className="text-slate-400 normal-case font-normal">— where the physical keys hang in the office</span>
            </div>
            <input value={draft.key_tag_location} onChange={e => setDraft({...draft, key_tag_location: e.target.value})}
              placeholder="e.g. Board A · Hook 12  ·  Key Box #47  ·  Front desk drawer"
              data-testid="unit-access-key-tag-input"
              className="w-full h-10 px-3 border-2 border-[#00C4CC] font-mono font-black text-sm bg-cyan-50"/>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Lockbox</div>
              <input value={draft.lockbox_code} onChange={e => setDraft({...draft, lockbox_code: e.target.value})}
                placeholder="e.g. 4291"
                data-testid="unit-access-lockbox-input"
                className="w-full h-10 px-3 border-2 border-slate-300 font-mono font-black text-sm"/>
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Entry Code</div>
              <input value={draft.entry_code} onChange={e => setDraft({...draft, entry_code: e.target.value})}
                placeholder="e.g. #1234"
                data-testid="unit-access-entry-input"
                className="w-full h-10 px-3 border-2 border-slate-300 font-mono font-black text-sm"/>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Entry Notes</div>
            <textarea value={draft.entry_notes} onChange={e => setDraft({...draft, entry_notes: e.target.value})}
              placeholder="Side door · dog on premises · knock first"
              rows={2}
              data-testid="unit-access-notes-input"
              className="w-full px-3 py-2 border-2 border-slate-300 text-sm"/>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setEditing(false)} disabled={saving}
              data-testid="unit-access-cancel"
              className="h-10 px-4 border-2 border-slate-300 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={save} disabled={saving}
              data-testid="unit-access-save"
              className="flex-1 h-10 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
              {saving ? "Saving…" : "Save Access Codes"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function PMNotesCard({ address, unit, pm_notes_current, pm_notes_log, onSaved }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const save = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const r = await api.post("/properties/unit-pm-note", { address, unit, note: draft.trim() });
      const nextLog = [r.data.entry, ...(pm_notes_log || [])];
      onSaved?.({ pm_notes_current: draft.trim(), pm_notes_log: nextLog });
      setDraft("");
      toast.success("PM note added");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };
  const log = pm_notes_log || [];
  const shown = showAll ? log : log.slice(0, 3);
  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-amber-500" data-testid="unit-pm-notes-card">
      <div className="flex items-center gap-2">
        <span className="text-amber-500 font-black">📝</span>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">PM Notes</h3>
        {log.length > 0 && <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">· {log.length} entr{log.length === 1 ? "y" : "ies"}</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <textarea value={draft} onChange={e => setDraft(e.target.value)}
          rows={2}
          placeholder="Add a note for property managers · e.g. 'Tenant works nights — schedule after 3pm' or 'Owner wants tile replaced, not patched.'"
          data-testid="unit-pm-note-input"
          className="flex-1 px-3 py-2 border-2 border-slate-300 text-sm"/>
        <button onClick={save} disabled={saving || !draft.trim()}
          data-testid="unit-pm-note-save"
          className="self-stretch px-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest">
          {saving ? "Saving…" : "Add Note"}
        </button>
      </div>
      {log.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="unit-pm-note-log">
          {shown.map(e => (
            <div key={e.id} className="p-2 bg-slate-50 border-l-2 border-amber-300 text-sm">
              <div className="text-slate-800 whitespace-pre-wrap">{e.text}</div>
              <div className="mt-1 text-[10px] font-black uppercase tracking-widest text-slate-500">
                {e.by || "—"} · {(e.at || "").slice(0, 16).replace("T", " ")}
              </div>
            </div>
          ))}
          {log.length > 3 && (
            <button onClick={() => setShowAll(!showAll)}
              data-testid="unit-pm-note-toggle"
              className="text-[10px] font-black uppercase tracking-widest text-amber-700 hover:text-amber-900">
              {showAll ? "Show less ↑" : `Show all ${log.length} ↓`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, multiline = false, testid }) {
  return (
    <div>
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 ${multiline ? "text-sm text-slate-700 whitespace-pre-wrap" : "font-mono font-black text-base"}`} data-testid={testid}>
        {value}
      </div>
    </div>
  );
}

// ── LEFT-BEHIND ITEMS on this unit ─────────────────────────────────
function UnitLeftBehind({ address, unit, visits }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    api.get("/left-behind", { params: { address, unit }})
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [address, unit]);

  const openCount = items.filter(i => i.status === "open").length;
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const withinTwo = new Date(Date.now() + 2 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const imminentVisit = (visits || []).find(v => (v.date || "") >= today && (v.date || "") <= withinTwo);
  const showWarn = openCount > 0 && imminentVisit;

  const submit = async () => {
    if (!desc.trim()) { toast.error("Add a description"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("address", address);
      fd.append("unit", unit);
      fd.append("description", desc);
      if (file) fd.append("file", file);
      await api.post("/left-behind", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Logged");
      setDesc(""); setFile(null); setAdding(false);
      if (inputRef.current) inputRef.current.value = "";
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed")); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/left-behind/${id}`, { status });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`/left-behind/${id}`);
      setItems(items.filter(x => x.id !== id));
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-amber-500"
      data-testid="unit-left-behind">
      <div className="flex items-center gap-2 flex-wrap">
        <Package weight="fill" size={20} className="text-amber-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Items Left Behind</h3>
        {openCount > 0 && (
          <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-widest bg-amber-500 text-slate-900"
            data-testid="unit-lb-open-count">{openCount} open</span>
        )}
        <button onClick={() => setAdding(v => !v)} data-testid="unit-lb-add-toggle"
          className="ml-auto h-8 px-3 bg-amber-500 hover:bg-amber-400 text-slate-900 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
          <Plus weight="bold" size={12}/> {adding ? "Cancel" : "Log item"}
        </button>
      </div>

      {showWarn && (
        <div className="mt-2 p-2 bg-red-600 text-white text-xs font-black uppercase tracking-widest flex items-center gap-2"
          data-testid="unit-lb-move-in-warning">
          <Warning weight="fill" size={16}/>
          {openCount} unresolved item{openCount === 1 ? "" : "s"} · Visit scheduled {imminentVisit.date}
        </div>
      )}

      {adding && (
        <div className="mt-3 p-2 bg-amber-50 border-2 border-amber-500 space-y-2" data-testid="unit-lb-form">
          <textarea rows={2} value={desc} onChange={e => setDesc(e.target.value)}
            placeholder="e.g. Broken IKEA dresser and 3 boxes of clothes in bedroom"
            className="w-full px-2 py-1 border-2 border-slate-300 text-sm"
            data-testid="unit-lb-desc-input"/>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <label className="h-8 px-2 border-2 border-slate-900 hover:bg-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 cursor-pointer">
              <Camera size={11}/> {file ? file.name.slice(0, 20) : "Photo (optional)"}
              <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="unit-lb-file-input"/>
            </label>
            <button onClick={submit} disabled={busy}
              data-testid="unit-lb-save"
              className="h-8 px-4 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
              {busy ? "Saving…" : "Log it"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="mt-3 text-slate-500 text-xs">Loading…</div>
      ) : items.length === 0 ? (
        <div className="mt-3 text-slate-500 text-xs italic">
          Nothing logged for this unit. Tap <b>Log item</b> the moment you spot junk.
        </div>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map(it => {
            const isOpen = it.status === "open";
            return (
              <div key={it.id} data-testid={`unit-lb-item-${it.id}`}
                className={`flex items-start gap-2 p-2 border-2 ${isOpen ? "border-amber-400 bg-amber-50" : "border-slate-300 bg-slate-50 opacity-70"}`}>
                {it.photo_path ? (
                  <a href={fileUrl(it.photo_path)} target="_blank" rel="noopener noreferrer">
                    <img src={fileUrl(it.photo_path)} alt="" className="h-14 w-14 object-cover border border-slate-400"/>
                  </a>
                ) : (
                  <div className="h-14 w-14 bg-slate-200 border border-slate-400 flex items-center justify-center text-slate-400 text-[9px] text-center px-1">No photo</div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold break-words">{it.description}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mt-0.5">
                    {(it.created_at || "").slice(0, 10)} · {it.created_by || "—"} · <span className={isOpen ? "text-amber-700" : "text-emerald-700"}>{it.status}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => setStatus(it.id, isOpen ? "resolved" : "open")}
                    data-testid={`unit-lb-toggle-${it.id}`}
                    className={`h-7 px-2 text-[9px] font-black uppercase tracking-widest ${isOpen ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-white border-2 border-slate-400 text-slate-600 hover:border-slate-900"}`}>
                    {isOpen ? "Resolve" : "Reopen"}
                  </button>
                  <button onClick={() => remove(it.id)}
                    data-testid={`unit-lb-delete-${it.id}`}
                    className="h-7 w-full border-2 border-red-600 text-red-600 hover:bg-red-50 inline-flex items-center justify-center">
                    <Trash size={11}/>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


const VISIT_KIND_BADGE = {
  move_in:    { label: "Move-in",    bg: "bg-emerald-600 text-white" },
  move_out:   { label: "Move-out",   bg: "bg-amber-500 text-white" },
  inspection: { label: "Inspection", bg: "bg-blue-600 text-white" },
  repair:     { label: "Repair",     bg: "bg-red-600 text-white" },
  access:     { label: "Access",     bg: "bg-slate-700 text-white" },
  other:      { label: "Visit",      bg: "bg-slate-500 text-white" },
};

function UnitScheduledVisits({ visits }) {
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })();
  const upcoming = (visits || []).filter(v => (v.date || "") >= today);
  const past = (visits || []).filter(v => (v.date || "") < today);
  const next = upcoming[0];

  const fmt = (d) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    const dt = new Date(+y, +m - 1, +day);
    return dt.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <div className="mt-4 rugged-card p-4 bg-white border-l-4 border-emerald-500"
      data-testid="unit-scheduled-visits">
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarBlank weight="fill" size={20} className="text-emerald-600"/>
        <h3 className="font-display font-black text-sm uppercase tracking-wider">Scheduled Visits</h3>
        {next && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${(VISIT_KIND_BADGE[next.kind] || VISIT_KIND_BADGE.other).bg}`}
            data-testid="unit-next-visit-chip">
            Next: {fmt(next.date)} · {(VISIT_KIND_BADGE[next.kind] || VISIT_KIND_BADGE.other).label}
          </span>
        )}
        <span className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500">
          {upcoming.length} upcoming · {past.length} past
        </span>
      </div>
      <div className="mt-3 space-y-1">
        {(visits || []).slice().sort((a,b) => (a.date || "").localeCompare(b.date || ""))
          .map(v => {
            const s = VISIT_KIND_BADGE[v.kind] || VISIT_KIND_BADGE.other;
            const isPast = (v.date || "") < today;
            return (
              <Link key={v.id} to={`/admin/projects/${v.project_id}`}
                data-testid={`unit-visit-${v.id}`}
                className="flex items-center gap-2 p-2 border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                <span className={`px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${s.bg} ${isPast ? "opacity-50" : ""}`}>
                  {s.label}
                </span>
                <div className={`font-black text-sm ${isPast ? "text-slate-400 line-through" : "text-slate-900"}`}>
                  {fmt(v.date)}
                </div>
                {v.note && <div className="text-xs text-slate-600 italic truncate">· {v.note}</div>}
                <div className="ml-auto text-[10px] font-black uppercase tracking-widest text-slate-500 truncate max-w-[160px]">
                  {v.project_name}
                </div>
                <ArrowSquareOut weight="bold" size={12} className="text-slate-400 flex-shrink-0"/>
              </Link>
            );
          })}
      </div>
    </div>
  );
}


/* ─── Add-picker + Manual Add Asset/Meter modals ────────────────────
   The "+ Add" button on the Assets card opens the picker; pick opens
   the specific modal. Both post through existing endpoints:
     - POST /property-assets   (also accepts a photo_path)
     - POST /property-meters   (with unit + serves_units for scoping)
   ──────────────────────────────────────────────────────────────── */
function AddPickerModal({ onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] w-full max-w-sm p-5" onClick={e=>e.stopPropagation()} data-testid="unit-add-picker">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Add to this unit</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">What are you adding?</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={()=>onPick("asset")} data-testid="add-picker-asset"
            className="flex flex-col items-center justify-center h-28 border-2 border-[#0f172a] hover:bg-fuchsia-50 transition-colors">
            <Package weight="fill" size={32} className="text-fuchsia-600"/>
            <div className="mt-1 font-black text-sm uppercase tracking-wider">Asset</div>
            <div className="text-[10px] text-slate-500">HVAC · Appliance · Boiler</div>
          </button>
          <button onClick={()=>onPick("meter")} data-testid="add-picker-meter"
            className="flex flex-col items-center justify-center h-28 border-2 border-[#0f172a] hover:bg-sky-50 transition-colors">
            <Broadcast weight="fill" size={32} className="text-sky-600"/>
            <div className="mt-1 font-black text-sm uppercase tracking-wider">Meter</div>
            <div className="text-[10px] text-slate-500">Gas · Electric · Water</div>
          </button>
        </div>
      </div>
    </div>
  );
}

function AddAssetModal({ address, unit, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", type: "", make: "", model: "", serial: "",
    location: "", notes: "", install_date: "",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() && !f.type.trim() && !f.make.trim()) {
      toast.error("Give it at least a name, type, or make");
      return;
    }
    setBusy(true);
    try {
      await api.post("/property-assets", {
        address, unit,
        name: (f.name.trim() || `${f.type || "Appliance"}${f.make ? " · " + f.make : ""}`).slice(0, 80),
        type: f.type.trim(),
        make: f.make.trim(),
        model: f.model.trim(),
        serial: f.serial.trim(),
        location: f.location.trim() || `${address}${unit ? ` · Unit ${unit}` : ""}`,
        notes: f.notes.trim(),
        photo_path: "",
      });
      toast.success("Asset added");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not save asset"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()}
        className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-5 my-8"
        data-testid="add-asset-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Add Asset</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Saves to <b>{address}{unit ? ` · Unit ${unit}` : ""}</b>. For scanning nameplates, use the pink <b>Scan Nameplate</b> button.
        </p>
        <div className="mt-3 space-y-2.5">
          <Row label="Name (short)">
            <input value={f.name} onChange={upd("name")} placeholder="e.g. Kitchen fridge" className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-name"/>
          </Row>
          <Row label="Type">
            <select value={f.type} onChange={upd("type")} className="w-full h-10 border-2 border-[#0f172a] px-2 bg-white" data-testid="asset-type">
              <option value="">— pick —</option>
              {["Refrigerator","Stove","Oven","Microwave","Dishwasher","Washer","Dryer","HVAC","Boiler","Water heater","Furnace","AC condenser","Sump pump","Fire extinguisher","Smoke detector","Camera","Router","Other"].map(t=><option key={t} value={t}>{t}</option>)}
            </select>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Make"><input value={f.make} onChange={upd("make")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-make"/></Row>
            <Row label="Model"><input value={f.model} onChange={upd("model")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-model"/></Row>
          </div>
          <Row label="Serial number"><input value={f.serial} onChange={upd("serial")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-serial"/></Row>
          <Row label="Location (where in the unit)">
            <input value={f.location} onChange={upd("location")} placeholder={unit ? `Unit ${unit} · Kitchen` : "Kitchen"} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-location"/>
          </Row>
          <Row label="Install date">
            <input type="date" value={f.install_date} onChange={upd("install_date")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="asset-install-date"/>
          </Row>
          <Row label="Notes"><textarea rows={2} value={f.notes} onChange={upd("notes")} className="w-full border-2 border-[#0f172a] px-2 py-1" data-testid="asset-notes"/></Row>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-10 border-2 border-[#0f172a] font-black uppercase text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="flex-1 h-10 bg-[#0f172a] text-white font-black uppercase text-xs disabled:opacity-50" data-testid="asset-save">{busy?"Saving…":"Save"}</button>
        </div>
      </form>
    </div>
  );
}

const METER_LOCATIONS = [
  "Basement", "Basement (via Unit 1)", "Utility closet", "Hallway",
  "Exterior wall", "Roof", "Kitchen", "Garage", "Other",
];

function AddMeterModal({ address, unit, siblingUnits, onClose, onSaved }) {
  const [f, setF] = useState({
    utility_type: "electric", provider: "", meter_number: "", service_id: "",
    reading: "", location: "", notes: "",
    unit: unit || "",
    serves_units: unit && unit !== "Common" ? [unit] : [],
    serves_whole_building: unit === "Common",
  });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const [busy, setBusy] = useState(false);

  const toggleServes = (u) => {
    setF(prev => {
      const cur = new Set(prev.serves_units || []);
      if (cur.has(u)) cur.delete(u); else cur.add(u);
      return { ...prev, serves_units: [...cur] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.provider && !f.meter_number && !f.reading) {
      toast.error("Enter at least a provider, meter #, or reading");
      return;
    }
    setBusy(true);
    try {
      await api.post("/property-meters", {
        address,
        utility_type: f.utility_type,
        provider: f.provider, meter_number: f.meter_number,
        service_id: f.service_id, location: f.location,
        reading: f.reading, notes: f.notes,
        unit: f.unit,
        serves_units: f.serves_whole_building ? [] : f.serves_units,
      });
      toast.success("Meter logged");
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail ?? "Could not save meter"));
    } finally { setBusy(false); }
  };

  const chipUnits = [...(siblingUnits || [])];
  if (unit && !chipUnits.some(u => u.toLowerCase() === unit.toLowerCase())) chipUnits.unshift(unit);

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()}
        className="w-full max-w-md bg-white border-2 border-[#0f172a] shadow-[6px_6px_0px_#0f172a] p-5 my-8"
        data-testid="add-meter-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-black text-xl uppercase">Log Meter</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 border-2 border-[#0f172a] font-black">×</button>
        </div>
        <p className="mt-1 text-xs text-slate-500 font-medium">
          Saves to <b>{address}</b>. Tag which units it serves so it shows up on their pages too.
        </p>
        <div className="mt-3 space-y-2.5">
          <Row label="Utility type">
            <select value={f.utility_type} onChange={upd("utility_type")} className="w-full h-10 border-2 border-[#0f172a] px-2 bg-white" data-testid="meter-utility">
              {[["electric","Electric"],["gas","Gas"],["water","Water"],["sewer","Sewer"],["other","Other"]].map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </Row>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Provider"><input value={f.provider} onChange={upd("provider")} placeholder="PECO / PGW" className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="meter-provider"/></Row>
            <Row label="Meter #"><input value={f.meter_number} onChange={upd("meter_number")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="meter-number"/></Row>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Row label="Service ID"><input value={f.service_id} onChange={upd("service_id")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="meter-service"/></Row>
            <Row label="Reading"><input value={f.reading} onChange={upd("reading")} className="w-full h-10 border-2 border-[#0f172a] px-2" data-testid="meter-reading"/></Row>
          </div>
          <Row label="Where is it physically?">
            <div className="flex flex-wrap gap-1">
              {METER_LOCATIONS.map(loc => (
                <button type="button" key={loc}
                  onClick={()=>setF({...f, location: loc})}
                  className={`px-2 py-1 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest ${f.location === loc ? "bg-[#0f172a] text-white" : "bg-white hover:bg-slate-100"}`}
                  data-testid={`meter-loc-preset-${loc.replace(/\W+/g,'-').toLowerCase()}`}>
                  {loc}
                </button>
              ))}
            </div>
            <input value={f.location} onChange={upd("location")} placeholder="Or type a specific spot" className="mt-2 w-full h-10 border-2 border-[#0f172a] px-2" data-testid="meter-location"/>
          </Row>
          <Row label="Unit the meter lives at">
            <select value={f.unit} onChange={upd("unit")} className="w-full h-10 border-2 border-[#0f172a] px-2 bg-white" data-testid="meter-unit">
              <option value="">— none —</option>
              {chipUnits.map(u => <option key={u} value={u}>{u === "Common" ? "Common area" : `Unit ${u}`}</option>)}
              {!chipUnits.some(u=>u.toLowerCase()==="common") && <option value="Common">Common area</option>}
            </select>
          </Row>
          <Row label="Serves which units?">
            <label className="flex items-center gap-2 mb-1.5">
              <input type="checkbox" checked={f.serves_whole_building}
                onChange={e=>setF({...f, serves_whole_building: e.target.checked})}
                data-testid="meter-serves-whole"/>
              <span className="text-xs font-bold">Whole building</span>
            </label>
            {!f.serves_whole_building && (
              <div className="flex flex-wrap gap-1">
                {chipUnits.filter(u=>u.toLowerCase()!=="common").map(u => (
                  <button type="button" key={u}
                    onClick={()=>toggleServes(u)}
                    className={`px-2 py-1 border-2 border-[#0f172a] text-[10px] font-black uppercase tracking-widest ${(f.serves_units || []).includes(u) ? "bg-emerald-600 text-white" : "bg-white hover:bg-emerald-50"}`}
                    data-testid={`meter-serves-${u.toLowerCase()}`}>
                    {u}
                  </button>
                ))}
                {chipUnits.filter(u=>u.toLowerCase()!=="common").length === 0 && (
                  <div className="text-xs text-slate-500 italic">No sibling units set up yet. Add units on the Properties page first.</div>
                )}
              </div>
            )}
          </Row>
          <Row label="Notes"><textarea rows={2} value={f.notes} onChange={upd("notes")} className="w-full border-2 border-[#0f172a] px-2 py-1" data-testid="meter-notes"/></Row>
        </div>
        <div className="mt-4 flex gap-2">
          <button type="button" onClick={onClose} className="flex-1 h-10 border-2 border-[#0f172a] font-black uppercase text-xs">Cancel</button>
          <button type="submit" disabled={busy} className="flex-1 h-10 bg-[#0f172a] text-white font-black uppercase text-xs disabled:opacity-50" data-testid="meter-save">{busy?"Saving…":"Save meter"}</button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-0.5">{label}</div>
      {children}
    </label>
  );
}

